import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import RouteOutlined from '@mui/icons-material/RouteOutlined'
import TimelineOutlined from '@mui/icons-material/TimelineOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { common } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ReactComponent as CSMarker } from 'assets/map/CSMarker.svg'
import { GOOGLE_MAPS_MAX_ZOOM } from 'components/common/map/constants'
import MapControlsCluster from 'components/common/map/MapControlsCluster'
import LocationMapSkeleton from 'components/customer-details/content/location/LocationMapSkeleton'
import { MARKER_TYPE } from 'components/customer-details/content/location/settings'
import ContainerMarker from 'components/inventory/containers/tabpanel/map/ContainerMarker'
import ContainerMarkerV2 from 'components/inventory/containers/tabpanel/map/ContainerMarkerV2'
import Polyline from 'components/route-manager/RouteManagerMapV2/shapes/Polyline'
import Constants from 'Constants'
import T from 'T'
import { isValidHex, isValidLatitudeRange, isValidLongitudeRange } from 'utils/validations'

const { GOOGLE_MAPS_API_KEY } = Constants

type PathContainerPin = {
  id?: string
  latitude?: number | string | null
  longitude?: number | string | null
}

// Canonical shape from parseDriverPathSegments / useRoutesDriverPathBreadcrumb.
type DriverPathSegment = {
  coordinates: number[][]
  driver?: string
}

type DriverPathSegmentInput = DriverPathSegment | number[][]

const getSegmentCoordinates = (segment: DriverPathSegmentInput): number[][] | null => {
  if (Array.isArray(segment) && segment.length > 0 && Array.isArray(segment[0])) {
    return segment.length >= 2 ? segment : null
  }
  const coords = (segment as DriverPathSegment).coordinates
  return Array.isArray(coords) && coords.length >= 2 ? coords : null
}

// GeoJSON Point feature from the route stops geojson (`${routeId}.stops.features`).
type RouteStopFeature = {
  geometry?: { coordinates?: number[] }
  properties?: { color?: string; sequence?: number | string }
}

type LocationMapImageV2Props = {
  width?: number
  latitude?: string | number | null
  longitude?: string | number | null
  // number (px) or a CSS length — the expanded WO map passes a viewport-relative height.
  height: number | string
  dynamicWidth?: boolean
  containerId?: string
  showInvalidAlert?: boolean
  showPlaceholder?: boolean
  zoom?: number
  markerType?: string
  containerData?: object
  enableTypeToggle?: boolean
  gestureHandling?: string
  // Driver-path overlay (WO detail): one [lng,lat][] per contiguous GPS segment + the containers to pin.
  driverPathSegments?: ReadonlyArray<DriverPathSegmentInput>
  pathContainers?: ReadonlyArray<PathContainerPin>
  // Route stop points for the WO's route/date (small sequence dots along the path).
  routeStopFeatures?: ReadonlyArray<RouteStopFeature>
  // Route sequence LineStrings ([lng,lat][] per route) — rendered as the shared dashed Polyline
  // (route-colored), matching the route-manager sequence line. Toggled via the shared layer control.
  routeSequenceLines?: ReadonlyArray<number[][]>
  // Show the shared fit-bounds button (frames the location + stop dots + containers). The WO
  // expanded map opts in; the collapsed card leaves its top-right corner to the expand button.
  enableFitBounds?: boolean
}

const ContainerMarkerV2Typed = ContainerMarkerV2 as React.ComponentType<{
  container: object
  color: string
  borderColor: string
  zIndex: number
}>

const PolylineTyped = Polyline as React.ElementType
// Boundary cast to the untyped JS control cluster (see docs/architecture/typescript.md).
const MapControlsClusterTyped = MapControlsCluster as React.ElementType

// ContainerMarker (untyped) does `parseFloat(container.latitude/longitude)` straight into
// AdvancedMarker's position with no finite-coord check — a NaN lat/lng throws synchronously and
// white-screens the page. LocationMapImageV2 is the shared entry point for pathContainers, so
// guard here even though the WO caller already pre-filters.
const hasValidPathCoords = (item: PathContainerPin): boolean =>
  isValidLatitudeRange(parseFloat(String(item.latitude ?? ''))) && isValidLongitudeRange(parseFloat(String(item.longitude ?? '')))

// center/zoom are uncontrolled (defaultCenter/defaultZoom) so the map stays pannable/zoomable —
// controlled props would snap the camera back on every parent re-render and fight the user's own
// gestures. But defaults only apply to the first map instance: when coords arrive after mount
// (the WO payload races the map), the camera is left wherever it was — worst case world view at
// zoom 0. Recenter + rezoom imperatively instead, once per genuine coordinate change. Overlays
// (driver path / stop dots) never drive the camera — the location is always the anchor.
const MapCameraController: React.FC<{
  lat: number
  lng: number
  zoom: number
}> = ({ lat, lng, zoom }) => {
  const map = useMap()
  const movedKeyRef = useRef<string | null>(null)
  const moveKey = `${lat},${lng},${zoom}`

  useEffect(() => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return
    if (movedKeyRef.current === moveKey) return
    movedKeyRef.current = moveKey
    map.setCenter({ lat, lng })
    map.setZoom(zoom)
  }, [map, lat, lng, zoom, moveKey])

  return null
}

// Layer keys/labels/icons match the canonical route-manager toggle set (see the Bulk map's
// LAYER_TOGGLE_ITEMS) so the WO map's controls read identically to every other Google map.
const STOPS_LAYER = 'stops'
const SEQUENCE_LAYER = 'sequence'
const DRIVER_LAYER = 'driver'

type LatLng = { lat: number; lng: number }
type LayerItem = { value: string; label: string; icon: React.ReactNode; tooltip: string }

// The canonical map controls (shared MapControlsCluster: layer toggles top-left, SAT/MAP thumbnail
// bottom-left, fit-bounds top-right). Rendered inside <Map> so it can resolve the map instance for
// the fit-bounds handler.
const LocationMapControls: React.FC<{
  isSatellite: boolean
  onBasemapToggle: () => void
  layerItems: LayerItem[]
  activeLayers: string[]
  onLayersChange: (next: string[]) => void
  fitBoundsPoints: ReadonlyArray<LatLng>
}> = ({ isSatellite, onBasemapToggle, layerItems, activeLayers, onLayersChange, fitBoundsPoints }) => {
  const map = useMap()

  const handleFitBounds = useCallback(() => {
    if (!map || fitBoundsPoints.length === 0) return
    const latitudes = fitBoundsPoints.map(point => point.lat)
    const longitudes = fitBoundsPoints.map(point => point.lng)
    map.fitBounds(
      { north: Math.max(...latitudes), south: Math.min(...latitudes), east: Math.max(...longitudes), west: Math.min(...longitudes) },
      48
    )
  }, [map, fitBoundsPoints])

  return (
    <MapControlsClusterTyped
      activeLayers={activeLayers}
      basemapBaseStyle="roadmap"
      isSatellite={isSatellite}
      layerItems={layerItems}
      onBasemapToggle={onBasemapToggle}
      onFitBounds={fitBoundsPoints.length > 0 ? handleFitBounds : undefined}
      onLayersChange={onLayersChange}
    />
  )
}

const LocationMapImageV2: React.FC<LocationMapImageV2Props> = ({
  showInvalidAlert = true,
  latitude,
  longitude,
  width,
  height,
  dynamicWidth = false,
  containerId,
  showPlaceholder = false,
  zoom = 16,
  markerType = 'default',
  containerData = {},
  enableTypeToggle = false,
  gestureHandling = 'none',
  driverPathSegments = [],
  pathContainers = [],
  routeStopFeatures = [],
  routeSequenceLines = [],
  enableFitBounds = false,
}) => {
  const theme = useTheme()
  const [mapTypeId, setMapTypeId] = useState('satellite')
  // Stops + driver path are the primary WO overlays → default on; the sequence line is secondary
  // context (noisy on dense routes) → default off. All three are user-togglable via the controls.
  const [showStops, setShowStops] = useState(true)
  const [showDriverPath, setShowDriverPath] = useState(true)
  const [showSequenceLines, setShowSequenceLines] = useState(false)
  const lat = parseFloat(String(latitude ?? ''))
  const lng = parseFloat(String(longitude ?? ''))
  const hasValidCoords = isValidLatitudeRange(lat) && isValidLongitudeRange(lng)
  const safePathContainers = useMemo(() => pathContainers.filter(hasValidPathCoords), [pathContainers])

  const safeStopFeatures = useMemo(
    () =>
      routeStopFeatures.filter(feature => {
        const [stopLng, stopLat] = feature?.geometry?.coordinates ?? []
        return isValidLatitudeRange(Number(stopLat)) && isValidLongitudeRange(Number(stopLng))
      }),
    [routeStopFeatures]
  )

  // The stop dots already carry the route color — the sequence line borrows it so both read as
  // one system (falls back to primary when the BE color is absent/free-form).
  const sequenceLineColor = useMemo(() => {
    const rawColor = String(safeStopFeatures[0]?.properties?.color ?? '')
    return isValidHex(rawColor) ? rawColor : theme.palette.primary.main
  }, [safeStopFeatures, theme])

  // Fit-bounds frames the location together with the overlays; only meaningful when overlays
  // exist, so the button hides (empty array) when there are none.
  const fitBoundsPoints = useMemo(() => {
    if (!enableFitBounds) return []
    const overlayPoints = [
      ...safeStopFeatures.map(feature => {
        const [stopLng, stopLat] = feature.geometry?.coordinates ?? []
        return { lat: Number(stopLat), lng: Number(stopLng) }
      }),
      ...safePathContainers.map(pathContainer => ({
        lat: parseFloat(String(pathContainer.latitude)),
        lng: parseFloat(String(pathContainer.longitude)),
      })),
    ]
    return overlayPoints.length > 0 ? [{ lat, lng }, ...overlayPoints] : []
  }, [enableFitBounds, safeStopFeatures, safePathContainers, lat, lng])

  const handleBasemapToggle = useCallback(() => setMapTypeId(current => (current === 'satellite' ? 'roadmap' : 'satellite')), [])

  // Toggle set built from the overlays this map actually has — labels/icons/tooltips mirror the
  // canonical route-manager LAYER_TOGGLE_ITEMS so every Google map reads the same.
  const hasDriverPath = useMemo(() => driverPathSegments.some(segment => getSegmentCoordinates(segment)), [driverPathSegments])
  const layerItems = useMemo<LayerItem[]>(() => {
    const items: LayerItem[] = []
    if (safeStopFeatures.length > 0) {
      items.push({ value: STOPS_LAYER, label: 'Stops', icon: <PlaceOutlined fontSize="small" />, tooltip: 'Show stop pill markers' })
    }
    if (routeSequenceLines.length > 0) {
      items.push({ value: SEQUENCE_LAYER, label: T.SEQUENCE, icon: <RouteOutlined fontSize="small" />, tooltip: T.SEQUENCE_LINE })
    }
    if (hasDriverPath) {
      items.push({ value: DRIVER_LAYER, label: T.DRIVER_PATH, icon: <TimelineOutlined fontSize="small" />, tooltip: T.SHOW_DRIVER_PATH })
    }
    return items
  }, [safeStopFeatures.length, routeSequenceLines.length, hasDriverPath])

  const activeLayers = useMemo(() => {
    const active: string[] = []
    if (showStops) active.push(STOPS_LAYER)
    if (showSequenceLines) active.push(SEQUENCE_LAYER)
    if (showDriverPath) active.push(DRIVER_LAYER)
    return active
  }, [showStops, showSequenceLines, showDriverPath])

  const handleLayersChange = useCallback((next: string[]) => {
    setShowStops(next.includes(STOPS_LAYER))
    setShowSequenceLines(next.includes(SEQUENCE_LAYER))
    setShowDriverPath(next.includes(DRIVER_LAYER))
  }, [])

  // Pass controls via a single options object (mirrors MapViewV2). Setting `disableDefaultUI` and
  // `zoomControl` as separate <Map> props doesn't compose in @vis.gl — disableDefaultUI wins and the
  // zoom buttons never render; inside one options object Google's native merge keeps zoom.
  const mapOptions = useMemo(
    () => ({
      clickableIcons: false,
      disableDefaultUI: true,
      fullscreenControl: false,
      gestureHandling,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: enableTypeToggle,
    }),
    [enableTypeToggle, gestureHandling]
  )

  const mapCenter = useMemo(() => ({ lat, lng }), [lat, lng])
  const isInventoryContainer = markerType === MARKER_TYPE.INVENTORY_CONTAINER
  const isContainer = markerType === MARKER_TYPE.CONTAINER
  const isDefault = markerType === MARKER_TYPE.DEFAULT

  if (showInvalidAlert && !hasValidCoords) {
    return (
      <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center' }}>
        {T.INVALID_LAT_LONG}
      </Alert>
    )
  }

  if (!hasValidCoords) {
    return showPlaceholder ? <LocationMapSkeleton height={String(height)} width={width != null ? String(width) : '100%'} /> : null
  }

  return (
    <Box height={height} id={containerId} overflow="hidden" width={dynamicWidth ? '100%' : width}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY ?? ''}>
        <Map
          defaultCenter={mapCenter}
          defaultZoom={zoom}
          mapId={`map-${containerId ?? 'default'}`}
          mapTypeId={mapTypeId}
          maxZoom={GOOGLE_MAPS_MAX_ZOOM}
          {...mapOptions}
        >
          <MapCameraController lat={lat} lng={lng} zoom={zoom} />
          {enableTypeToggle && (
            <LocationMapControls
              activeLayers={activeLayers}
              fitBoundsPoints={fitBoundsPoints}
              isSatellite={mapTypeId === 'satellite'}
              layerItems={layerItems}
              onBasemapToggle={handleBasemapToggle}
              onLayersChange={handleLayersChange}
            />
          )}
          {isInventoryContainer && <ContainerMarker key={String((containerData as { id?: string }).id)} container={containerData} />}
          {isContainer && (
            <ContainerMarkerV2Typed
              borderColor={theme.palette.success.main}
              color={theme.palette.common.white}
              container={{ ...containerData, latitude: lat, longitude: lng }}
              zIndex={1}
            />
          )}
          {isDefault && (
            <AdvancedMarker position={mapCenter} zIndex={1}>
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: 2,
                }}
              >
                <SvgIcon sx={{ color: common.white, fontSize: 18 }}>
                  <CSMarker />
                </SvgIcon>
              </Box>
            </AdvancedMarker>
          )}
          {showSequenceLines &&
            routeSequenceLines.map(lineCoordinates => {
              const firstPoint = lineCoordinates[0]
              const lastPoint = lineCoordinates[lineCoordinates.length - 1]
              const lineKey = `${firstPoint?.[0]}-${firstPoint?.[1]}-${lastPoint?.[0]}-${lastPoint?.[1]}-${lineCoordinates.length}`
              // Shared dashed Polyline, matching the advanced map's WebGL sequence line
              // (useSequenceLineLayers): 0.8 opacity, 2px — 0.6 was too faint on satellite.
              return (
                <PolylineTyped
                  key={`route-line-${lineKey}`}
                  dashed
                  path={lineCoordinates.map(([lineLng, lineLat]) => ({ lat: lineLat, lng: lineLng }))}
                  strokeColor={sequenceLineColor}
                  strokeOpacity={0.8}
                  strokeWeight={2}
                  zIndex={5}
                />
              )
            })}
          {showDriverPath &&
            driverPathSegments.map(segment => {
              const coords = getSegmentCoordinates(segment)
              if (!coords) return null
              const lastPoint = coords[coords.length - 1]
              return (
                <PolylineTyped
                  key={`wo-path-${coords[0]?.[0]}-${coords[0]?.[1]}-${lastPoint?.[0]}-${lastPoint?.[1]}-${coords.length}`}
                  showArrows
                  path={coords.map(([segLng, segLat]) => ({ lat: segLat, lng: segLng }))}
                  strokeColor={theme.palette.primary.main}
                  strokeOpacity={0.9}
                  strokeWeight={4}
                />
              )
            })}
          {safePathContainers.map(pathContainer => (
            <ContainerMarker key={String(pathContainer.id)} container={pathContainer} />
          ))}
          {showStops &&
            safeStopFeatures.map(feature => {
              const [stopLng, stopLat] = feature.geometry?.coordinates ?? []
              const sequence = feature.properties?.sequence
              // getContrastText throws on a non-hex input, and the BE color is free-form.
              const rawColor = String(feature.properties?.color ?? '')
              const stopColor = isValidHex(rawColor) ? rawColor : theme.palette.primary.main
              return (
                <AdvancedMarker
                  key={`route-stop-${sequence ?? ''}-${stopLat}-${stopLng}`}
                  position={{ lat: Number(stopLat), lng: Number(stopLng) }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: stopColor,
                      border: `1.5px solid ${common.white}`,
                      borderRadius: '50%',
                      boxShadow: 1,
                      color: theme.palette.getContrastText(stopColor),
                      display: 'flex',
                      fontSize: 10,
                      fontWeight: 600,
                      height: 18,
                      justifyContent: 'center',
                      width: 18,
                    }}
                  >
                    {sequence ?? ''}
                  </Box>
                </AdvancedMarker>
              )
            })}
        </Map>
      </APIProvider>
    </Box>
  )
}

export default LocationMapImageV2
